/**
 * @lines98/client/components/bonus-pill — ПРИМИТИВ «бонус-пилюля» (иконка ресурса + «+X%»).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §2 «примитив → components/»; owner-directive
 * 2026-06-14 «работаем строго через примитивы; если бонус инлайн в jewelry-card — ИЗВЛЕКИ
 * в примитив и используй в обоих»). Бонус-чип «иконка + +N%» был СВЁРСТАН ВРУЧНУЮ внутри
 * `jewelry-card` (звери) и `gnome-card` (гномы) — две копии одной и той же вёрстки. Это
 * ОДИН переиспользуемый контрол → один файл-примитив, обе карточки — его «ветки».
 *
 * **Два состояния (owner-эскиз):**
 *   · OPEN   — иконка ресурса (currency/gem-глиф) + акцентный «+N%» (главная «штука для
 *              любования»). Бонус активен.
 *   · LOCKED — приглушённая иконка + порог-метка «L5»/«L15» вместо процента (§6: не «0» без
 *              контекста, а уровень раскрытия). Бонус спит.
 *
 * **НИКАКОГО ВЕРХНЕГО КАПА (owner-directive 2026-06-14):** размеры иконки/текста — формулы
 * от `--cell-size` с нижним floor (читаемость), без потолка (§0.G.5). Процент — любой (число
 * не капается). Один источник класса `bonus-pill` + модификаторы вариантов.
 *
 * **Жёсткая совместимость baseline (jewelry не сломать):** разметка/классы СОВПАДАЮТ с
 * прежним инлайном звериной карточки (тот же `aria-label`-формат, та же структура icon+pct),
 * поэтому baseline-скриншоты Мастерской БАЙТ-В-БАЙТ те же. Карточки добавляют свой
 * surface-класс (`jewelry-card-bonus` / `gnome-card-bonus`) поверх `bonus-pill` (стиль/
 * раскладка-обёртка остаются в их co-located CSS), а ОБЩИЙ слой (icon-размер, pct-акцент,
 * locked-приглушение) живёт здесь в bonus-pill.css.
 *
 * a11y (§8): пилюля несёт `aria-label` (категория + значение/порог); иконка декоративна
 * (`aria-hidden`). Тактильность — это статусный чип (не кнопка), 44px не требуется.
 *
 * Cross-references:
 *   - components/jewelry-card.ts / gnome-card.ts (ветки-потребители).
 *   - components/meta-icons.ts (ICON_GOLD / ICON_SHARD / gemGlyph — иконки ресурсов).
 *   - bonus-pill.css (co-located; размеры от --cell-size, без капов; locked-приглушение).
 */

import './bonus-pill.css';

import { t } from '../lib/i18n/index.js';

export interface BonusPillOptions {
  /** Inline-SVG/`<img>` иконки ресурса (innerHTML) — currency/gem-глиф. */
  readonly icon: string;
  /** Доступная категория для aria-label (напр. «Золото» / «Осколки» / «Камень»). */
  readonly label: string;
  /** Доп. класс(ы) на корень — surface-обёртка (`jewelry-card-bonus` / `gnome-card-bonus`). */
  readonly className?: string;
  /** Доп. класс(ы) на иконку — surface-совместимость (`jewelry-card-bonus-ic` baseline). */
  readonly iconClassName?: string;
  /** Доп. класс(ы) на «+X%»-слот — surface-совместимость (`jewelry-card-bonus-pct` baseline). */
  readonly pctClassName?: string;
}

export interface BonusPillHandle {
  readonly element: HTMLElement;
  /**
   * Открытый бонус: показать готовую строку значения (акцент). `display` — УЖЕ
   * ОТФОРМАТИРОВАННАЯ строка «+X.X%» (один источник формата — `formatGnomeBonusPercent` в
   * lib/gnome-state; примитив презентационный, сам НЕ форматирует и НЕ округляет). Значение
   * НЕ капается (owner-directive «никакого верхнего капа»); длина любая — каркас не прыгает
   * (ширина слота резервируется CSS, не от текста). `ariaValue` — то же значение словами
   * для aria-label (напр. «+0,5 процента»); опущен → озвучивается `display`.
   */
  setOpen(display: string, ariaValue?: string): void;
  /** Закрытый бонус: приглушить + показать порог-метку («L5»/«L15»). */
  setLocked(unlockLevel: number): void;
}

/**
 * Построить бонус-пилюлю [иконка ресурса + «+X%»-слот]. Возвращает handle; потребитель
 * (карточка) вставляет `handle.element` и переключает open/locked по уровню.
 */
export function createBonusPill(opts: BonusPillOptions): BonusPillHandle {
  const root = document.createElement('span');
  root.className = 'bonus-pill';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  const icon = document.createElement('span');
  icon.className = 'bonus-pill-ic';
  if (opts.iconClassName) icon.classList.add(...opts.iconClassName.split(/\s+/).filter(Boolean));
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = opts.icon;

  // Значение-слот: «+X.X%» (open) ИЛИ порог «L?» (locked). Один ПОСТОЯННЫЙ элемент — при
  // апгрейде/престиже меняется только textContent (instant-hydration: не пересборка узла).
  const value = document.createElement('span');
  value.className = 'bonus-pill-pct';
  if (opts.pctClassName) value.classList.add(...opts.pctClassName.split(/\s+/).filter(Boolean));

  root.append(icon, value);

  return {
    element: root,
    setOpen(display: string, ariaValue?: string): void {
      root.classList.remove('bonus-pill--locked');
      value.classList.remove('bonus-pill-pct--lock');
      // Готовая строка значения (форматирует вызывающий, един источник). НЕ капается.
      value.textContent = display;
      root.setAttribute(
        'aria-label',
        t('game.meta.bonusAria', { label: opts.label, value: ariaValue ?? display }),
      );
    },
    setLocked(unlockLevel: number): void {
      root.classList.add('bonus-pill--locked');
      value.classList.add('bonus-pill-pct--lock');
      value.textContent = `L${unlockLevel}`;
      root.setAttribute(
        'aria-label',
        t('game.meta.bonusLockedAria', { label: opts.label, level: unlockLevel }),
      );
    },
  };
}
